/**
 * FIELD VISIT SETTINGS DEFAULTS
 * 
 * Default configuration for field-level requirements
 * Can be customized per company in Settings
 */

export const DEFAULT_FIELD_VISIT_SETTINGS = {
  // STEP 1: Client Intel
  step1_fields: {
    contact_name: { enabled: true, required: true, label: 'Contact Name' },
    contact_phone: { enabled: true, required: true, label: 'Contact Phone' },
    contact_email: { enabled: true, required: false, label: 'Contact Email' },
    preferred_contact_method: { enabled: true, required: false, label: 'Preferred Contact Method' },
    best_time_to_reach: { enabled: true, required: false, label: 'Best Time to Reach' },
    decision_makers: { enabled: true, required: true, label: 'Decision Makers' },
    spouse_involved: { enabled: true, required: false, label: 'Spouse Involved' },
    referral_source: { enabled: true, required: false, label: 'Referral Source' },
    other_quotes: { enabled: true, required: false, label: 'Getting Other Quotes' },
    property_type: { enabled: true, required: true, label: 'Property Type' },
    property_ownership: { enabled: true, required: false, label: 'Property Ownership' },
    property_age: { enabled: true, required: false, label: 'Property Age' },
    property_sqft: { enabled: true, required: false, label: 'Property Square Footage' },
    hoa_present: { enabled: true, required: false, label: 'HOA Present' },
  },

  // STEP 2: Client Discovery
  step2_fields: {
    problem_description: { enabled: true, required: true, label: 'Problem Description' },
    why_now: { enabled: true, required: true, label: 'Why Now' },
    success_definition: { enabled: true, required: true, label: 'Success Definition' },
    budget_range: { enabled: true, required: true, label: 'Budget Range' },
    budget_flexibility: { enabled: true, required: false, label: 'Budget Flexibility' },
    timeline_desired_start: { enabled: true, required: true, label: 'Desired Start Date' },
    timeline_urgency: { enabled: true, required: false, label: 'Timeline Urgency' },
    timeline_hard_deadline: { enabled: true, required: false, label: 'Hard Deadline' },
    staying_during_work: { enabled: true, required: false, label: 'Staying During Work' },
    occupants_info: { enabled: true, required: false, label: 'Occupants Info' },
    pets_info: { enabled: true, required: false, label: 'Pets Info' },
    work_from_home: { enabled: true, required: false, label: 'Work From Home' },
  },

  // STEP 3: Decision Drivers
  step3_fields: {
    priority: { enabled: true, required: true, label: 'Top Priority' },
    priority_explanation: { enabled: true, required: false, label: 'Priority Explanation' },
    biggest_concern: { enabled: true, required: true, label: 'Biggest Concern' },
    biggest_concern_explanation: { enabled: true, required: false, label: 'Concern Explanation' },
    must_haves: { enabled: true, required: false, label: 'Must Haves' },
    nice_to_haves: { enabled: true, required: false, label: 'Nice to Haves' },
    deal_breakers: { enabled: true, required: false, label: 'Deal Breakers' },
  },

  // STEP 4: Measurements
  step4_settings: {
    min_areas_required: 1,
    room_dimensions_required: true,
    lidar_upload_enabled: true,
    polycam_enabled: true,
    hover_enabled: true,
  },

  // STEP 5: Photos
  step5_settings: {
    min_photos_required: 1,
    photo_required_per_replacement: true,  // LIABILITY FEATURE
    overview_photo_required: true,
    max_file_size_mb: 10,
  },

  // STEP 6: Product Selection
  step6_settings: {
    product_required_for_replacement: true,
    allow_client_supplied: true,
    allow_special_order: true,
    show_tier_labels: true,
    default_tier: 'mid',
  },

  // STEP 7: Risk Ledger
  step7_settings: {
    min_risks_required: 1,
    description_mandatory: true,        // If risk selected, must describe
    cost_impact_enabled: true,
    mitigation_plan_enabled: true,
    risk_owner_enabled: true,
  },

  // STEP 8: Scope Description
  step8_settings: {
    scope_narrative_required: true,
    min_characters: 100,
    ai_assist_enabled: true,
  },

  // Markup settings for catalog
  markup_settings: {
    default_percent: 20,
    by_tier: {
      builder: 15,
      mid: 20,
      premium: 25,
    },
  },
};

// Helper to get required fields for a step
export const getRequiredFieldsForStep = (step, settings = DEFAULT_FIELD_VISIT_SETTINGS) => {
  const stepKey = `step${step}_fields`;
  const fields = settings[stepKey];
  if (!fields) return [];
  
  return Object.entries(fields)
    .filter(([_, config]) => config.enabled && config.required)
    .map(([key, config]) => ({ key, ...config }));
};

// Helper to check if a field is required
export const isFieldRequired = (step, fieldKey, settings = DEFAULT_FIELD_VISIT_SETTINGS) => {
  const stepKey = `step${step}_fields`;
  const fields = settings[stepKey];
  if (!fields || !fields[fieldKey]) return false;
  return fields[fieldKey].enabled && fields[fieldKey].required;
};

export default { DEFAULT_FIELD_VISIT_SETTINGS, getRequiredFieldsForStep, isFieldRequired };
